<template>
  <el-dialog
    title="Crop avatar"
    :visible.sync="dialogVisible"
    width="520px"
    append-to-body
    :close-on-click-modal="false"
    @closed="onClosed"
  >
    <p class="cropHint">Drag to reposition · use the slider to zoom · output is square for Portfolio</p>
    <div
      ref="stage"
      class="cropStage"
      @mousedown.prevent="onPointerDown"
      @touchstart.prevent="onPointerDown"
    >
      <img
        v-show="imageLoaded"
        ref="image"
        class="cropImage"
        :src="sourceUrl"
        :style="imageStyle"
        draggable="false"
        @load="onImageLoad"
      >
      <div v-if="!imageLoaded" class="cropLoading">Loading image…</div>
      <div class="cropMask">
        <div class="cropWindow"></div>
      </div>
    </div>
    <div class="cropControls">
      <span class="cropControlLabel">Zoom</span>
      <el-slider v-model="zoom" :min="minZoom" :max="maxZoom" :step="0.01" @input="clampOffset" />
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">Cancel</el-button>
      <el-button type="primary" :loading="processing" :disabled="!imageLoaded" @click="confirmCrop">
        Save square avatar
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
const STAGE_SIZE = 360
const OUTPUT_SIZE = 512

export default {
  name: 'TeamCreditAvatarCropper',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sourceUrl: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      dialogVisible: false,
      imageLoaded: false,
      processing: false,
      naturalWidth: 0,
      naturalHeight: 0,
      zoom: 1,
      minZoom: 1,
      maxZoom: 3,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragOriginX: 0,
      dragOriginY: 0
    }
  },
  computed: {
    baseScale () {
      if (!this.naturalWidth || !this.naturalHeight) {
        return 1
      }
      return Math.max(STAGE_SIZE / this.naturalWidth, STAGE_SIZE / this.naturalHeight)
    },
    displayScale () {
      return this.baseScale * this.zoom
    },
    displayWidth () {
      return this.naturalWidth * this.displayScale
    },
    displayHeight () {
      return this.naturalHeight * this.displayScale
    },
    imageStyle () {
      return {
        width: this.displayWidth + 'px',
        height: this.displayHeight + 'px',
        transform: 'translate(' + this.offsetX + 'px, ' + this.offsetY + 'px)'
      }
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (value) {
        this.dialogVisible = value
        if (value) {
          this.resetState()
        }
      }
    },
    dialogVisible (value) {
      this.$emit('update:visible', value)
    },
    sourceUrl () {
      this.resetState()
    },
    zoom () {
      this.clampOffset()
    }
  },
  mounted () {
    window.addEventListener('mousemove', this.onPointerMove)
    window.addEventListener('mouseup', this.onPointerUp)
    window.addEventListener('touchmove', this.onPointerMove, { passive: false })
    window.addEventListener('touchend', this.onPointerUp)
  },
  beforeDestroy () {
    window.removeEventListener('mousemove', this.onPointerMove)
    window.removeEventListener('mouseup', this.onPointerUp)
    window.removeEventListener('touchmove', this.onPointerMove)
    window.removeEventListener('touchend', this.onPointerUp)
  },
  methods: {
    resetState () {
      this.imageLoaded = false
      this.processing = false
      this.naturalWidth = 0
      this.naturalHeight = 0
      this.zoom = 1
      this.minZoom = 1
      this.maxZoom = 3
      this.offsetX = 0
      this.offsetY = 0
      this.dragging = false
    },
    onImageLoad () {
      const img = this.$refs.image
      if (!img) {
        return
      }
      this.naturalWidth = img.naturalWidth
      this.naturalHeight = img.naturalHeight
      this.imageLoaded = true
      this.minZoom = 1
      this.maxZoom = Math.max(3, (Math.max(this.naturalWidth, this.naturalHeight) / Math.min(this.naturalWidth, this.naturalHeight)) * 1.5)
      this.zoom = 1
      this.centerImage()
    },
    centerImage () {
      this.offsetX = (STAGE_SIZE - this.displayWidth) / 2
      this.offsetY = (STAGE_SIZE - this.displayHeight) / 2
      this.clampOffset()
    },
    clampOffset () {
      const minX = Math.min(0, STAGE_SIZE - this.displayWidth)
      const minY = Math.min(0, STAGE_SIZE - this.displayHeight)
      const maxX = 0
      const maxY = 0
      this.offsetX = Math.min(maxX, Math.max(minX, this.offsetX))
      this.offsetY = Math.min(maxY, Math.max(minY, this.offsetY))
    },
    onPointerDown (event) {
      if (!this.imageLoaded) {
        return
      }
      const point = this.pointerPoint(event)
      this.dragging = true
      this.dragStartX = point.x
      this.dragStartY = point.y
      this.dragOriginX = this.offsetX
      this.dragOriginY = this.offsetY
    },
    onPointerMove (event) {
      if (!this.dragging) {
        return
      }
      if (event.cancelable) {
        event.preventDefault()
      }
      const point = this.pointerPoint(event)
      this.offsetX = this.dragOriginX + (point.x - this.dragStartX)
      this.offsetY = this.dragOriginY + (point.y - this.dragStartY)
      this.clampOffset()
    },
    onPointerUp () {
      this.dragging = false
    },
    pointerPoint (event) {
      const touch = event.touches && event.touches[0]
      if (touch) {
        return { x: touch.clientX, y: touch.clientY }
      }
      return { x: event.clientX, y: event.clientY }
    },
    confirmCrop () {
      const img = this.$refs.image
      if (!img || !this.imageLoaded) {
        return
      }
      this.processing = true
      try {
        const canvas = document.createElement('canvas')
        canvas.width = OUTPUT_SIZE
        canvas.height = OUTPUT_SIZE
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          throw new Error('Unable to prepare the cropped image.')
        }

        const scale = this.displayScale
        const sourceX = (0 - this.offsetX) / scale
        const sourceY = (0 - this.offsetY) / scale
        const sourceSize = STAGE_SIZE / scale

        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceSize,
          sourceSize,
          0,
          0,
          OUTPUT_SIZE,
          OUTPUT_SIZE
        )

        canvas.toBlob((blob) => {
          this.processing = false
          if (!blob) {
            this.$message.error('Unable to export the cropped avatar.')
            return
          }
          this.dialogVisible = false
          this.$emit('confirm', blob)
        }, 'image/jpeg', 0.92)
      } catch (error) {
        this.processing = false
        this.$message.error((error && error.message) || 'Crop failed. Please try again.')
      }
    },
    onClosed () {
      this.resetState()
      this.$emit('closed')
    }
  }
}
</script>

<style scoped>
.cropHint {
  margin: 0 0 12px;
  color: #667;
  font-size: 13px;
}

.cropStage {
  position: relative;
  width: 360px;
  height: 360px;
  margin: 0 auto;
  overflow: hidden;
  background: #eef3f8;
  border-radius: 8px;
  user-select: none;
  touch-action: none;
}

.cropImage {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: top left;
  will-change: transform;
}

.cropLoading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #889;
  font-size: 14px;
}

.cropMask {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.cropWindow {
  width: 100%;
  height: 100%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
  border: 2px solid rgba(255, 255, 255, 0.95);
  box-sizing: border-box;
}

.cropControls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.cropControlLabel {
  flex-shrink: 0;
  width: 42px;
  color: #556;
  font-size: 13px;
  font-weight: 600;
}

.cropControls .el-slider {
  flex: 1;
}
</style>
